import React, { useState } from 'react';
import { useCallback } from 'react';
import { usePostIdentidadeRefreshToken } from '../../../../data/api/gestao/identidade/post-refresh-token';
import { useGetPerfilById } from '../../../../data/api/gestao/perfil/get-perfil-by-id';
import { PlanoAtualModel } from '../../../../model/api/gestao/plano/plano-atual-model';
import { useGetPlanoAtual } from '../../../../data/api/gestao/plano/get-plano-atual';
import { useGetPoliticaValidar } from '../../../../data/api/gestao/politica/get-politica-validar';
import { ApiError } from '../../../../model/app/errors/api-erros';
import { TokenKdsEmpresaModel } from '../../../../model/api/gestao/master/token-kds-empresa';
import { KdsStorageKeys, useKdsStorage, useToastSaurus } from '../../use-cases';
import { TokenKdsModel } from '../../../../model/api/gestao/master/token-kds';
import { useKdsToken } from "../../../app";
import { MultiContratoModel } from '../../../../model/api/multi-contrato/multi-contrato';
import { isPlanoControleProducao } from '../../../../utils/plano-utils';
import { useAuth } from '../../use-cases/auth';
import { useGetConfigEmpresaId } from '../../../../data/api/gestao/empresa/get-config-empresa-id';
import { EmpresaCompletaModel } from '../../../../model/api/gestao/master/empresa-completa';
import { EmpresaModel } from '../../../../model/api/gestao/master/empresa';
import { useGetEmpresaId } from '../../../../data/api/gestao/empresa/get-empresa-id';
import { ConfigEmpresaModel } from '../../../../model/api/gestao/master/config-empresa-model';
import { useGetConfigContrato } from '../../../../data/api/gestao/contrato/get-contrato-configuracao';
import { RetornoApiModel } from '../../../../model';
import { useGetUsuarioPessoa } from '../../../../data/api/gestao/pessoa/get-usuario-pessoa';
import { ContratoConfigCompletoModel } from '../../../../model/api/gestao/master/contrato-completo-config';
import { EnumCodigosPermissoes } from '../../../../model/enums/enum-codigos-permissoes';
import { isEmpty } from 'lodash';
import { PerfilPermissaoModel } from '../../../../model/api/gestao/perfil';
import { PerfilModel } from '../../../../model/api/gestao/master/perfil';

interface SessaoAtualContextValue {
  usuario?: TokenKdsModel;
  plano?: PlanoAtualModel | undefined;
  getEmpresaSelecionada: () => TokenKdsEmpresaModel | undefined;
  getPerfil: () => Array<PerfilModel>;
  carregando: boolean;
  logar: (
    usuario: string,
    senha: string,
    contratoId: string,
    manterConectado: boolean,
  ) => Promise<string>;
  deslogar: (usuarioId: string) => void;
  setarUser: (token: string, manterConectado: boolean, empresaId?: string) => Promise<void>;
  carregarDadosUsuario: (user: TokenKdsModel) => Promise<void>;
  refreshUser: (refreshToken: string) => Promise<boolean>;
  sessaoExpirada: () => boolean;
  converterToken: (token: string) => TokenKdsModel | undefined;
  validaUsuarioConectado: () => boolean;
  termosDeUsoAtivo: () => boolean;
  selecionarEmpresa: (empresaId: string) => Promise<void>;
  refreshPlanoUsuario: (token?: string) => Promise<PlanoAtualModel | undefined>;
  getPermissaoBoolean: (codigo: EnumCodigosPermissoes) => boolean;
  getPermissaoString: (codigo: EnumCodigosPermissoes) => string;
}

const SessaoAtualContext = React.createContext<SessaoAtualContextValue>({
  usuario: undefined,
  carregando: false,
  getEmpresaSelecionada: () => undefined,
  getPerfil: () => new Array<PerfilModel>(),
  sessaoExpirada: () => false,
  logar: (usuario: string, senha: string, contratoId: string, manterConectado: boolean) => {
    return new Promise<string>(() => true);
  },
  converterToken: (token: string) => {
    return undefined;
  },
  setarUser: async () => { },
  deslogar: (usuarioId: string) => { },
  carregarDadosUsuario: (user: TokenKdsModel) => {
    return new Promise<void>(() => true);
  },
  refreshUser: (refreshToken: string) => {
    return new Promise<boolean>(() => true);
  },
  termosDeUsoAtivo: () => false,

  selecionarEmpresa: (empresaId: string) => new Promise<void>(() => { }),
  validaUsuarioConectado: () => false,
  refreshPlanoUsuario: (token?: string) => {
    return new Promise<PlanoAtualModel | undefined>(() => undefined);
  },
  getPermissaoBoolean: (codigo: EnumCodigosPermissoes) => false,
  getPermissaoString: (codigo: EnumCodigosPermissoes) => '',
});

export interface SessaoAtualProviderProps {
  children: React.ReactNode;
}

export const useSessaoAtual = () => React.useContext(SessaoAtualContext);

export const SessaoAtualProvider = ({ children }: SessaoAtualProviderProps) => {
  // PROVIDERS
  const {
    getTokenFromStorage,
    convertToken,
    getConnectedUsers,
    persistTermosDeUso,
    getEmpresaAtual,
    persistEmpresaSelecionada,
    getTermosDeUso,
    getPerfilPermissoes,
    persistPerfilPermissoes,
    isTokenValid,
    persistToken,
    addConnectedUser,
    removeConnectedUser,
    getPlanoUsuario,
    persistPlanoUsuario,
    persistPessoaUsuario,
    getPessoaUsuario,
  } = useKdsToken();
  const { delRegistro, getRegistro, setRegistro } = useKdsStorage();
  const { auth, carregando: carregandoAuth } = useAuth()
  const { showToast } = useToastSaurus()
  // STATES E REFS
  const [loadingManual, setLoadingManual] = useState(false);
  const [usuario, setarUsuario] = React.useState(getTokenFromStorage());
  const [plano, setPlano] = React.useState(getPlanoUsuario());

  // CHAMDAS API
  const { getPerfilById, carregando: carregarPerfilById } = useGetPerfilById();
  const { postIdentidadeRefreshToken, carregando: carregandoRefreshToken } =
    usePostIdentidadeRefreshToken();
  useGetPoliticaValidar();
  const { getPlanoAtual, carregando: carregandoPlanoAtual } =
    useGetPlanoAtual();
  const { getEmpresaId, carregando: carregandoEmpresaId } = useGetEmpresaId()
  const { getConfigEmpresaId, carregando: carregandoConfigEmpresa } =
    useGetConfigEmpresaId();
  const { getConfigContrato, carregando: carregandoConfigContrato } =
    useGetConfigContrato();
  const { getUsuarioPessoa, carregando: carregandoUsuarioPessoa } = useGetUsuarioPessoa()

  // AUX
  const loading =
    [
      carregandoRefreshToken,
      carregarPerfilById,
      loadingManual,
      carregandoPlanoAtual,
      carregandoAuth,
      carregandoConfigEmpresa,
      carregandoEmpresaId,
      carregandoConfigContrato,
      carregandoUsuarioPessoa,
    ].includes(true)


  const sessaoExpirada = React.useCallback((): boolean => {
    return !isTokenValid(usuario);
  }, [isTokenValid, usuario]
  );

  const converterToken = React.useCallback(
    (token: string): TokenKdsModel | undefined => {
      return convertToken(token);
    },
    [convertToken],
  );

  const termosDeUsoAtivo = React.useCallback((): boolean => {
    return getTermosDeUso();
  }, [getTermosDeUso]
  );

  const limparVariaveis = useCallback(() => {
    persistPlanoUsuario(undefined);
    persistTermosDeUso(undefined, undefined);
    setPlano(undefined);
    persistPerfilPermissoes(undefined);

    sessionStorage.clear();
  }, [persistPerfilPermissoes, persistPlanoUsuario, persistTermosDeUso]
  );

  const deslogar = React.useCallback(
    (usuarioId: string) => {
      removeConnectedUser(usuarioId || '');
      persistToken(undefined);
      setarUsuario(undefined);
      limparVariaveis();
    },
    [removeConnectedUser, persistToken, limparVariaveis],
  );

  const refreshPlanoUsuario = useCallback(async (token?: string): Promise<PlanoAtualModel | undefined> => {
    //Pega o plano atual
    try {
      const retPlano = await getPlanoAtual(token);
      if (retPlano.erro) {
        throw retPlano.erro;
      }

      if (retPlano.statusCode === 204) {
        persistPlanoUsuario(new PlanoAtualModel());
        setPlano(new PlanoAtualModel());
      } else {
        persistPlanoUsuario(retPlano.resultado?.data as PlanoAtualModel);
        setPlano(retPlano.resultado?.data as PlanoAtualModel);
        return retPlano.resultado?.data as PlanoAtualModel;
      }
    } catch (e: any) {
      throw e;
    }
  }, [getPlanoAtual, persistPlanoUsuario, setPlano]
  );

  const validaUsuarioConectado = useCallback(() => {
    const users = getConnectedUsers();
    return users.filter((x) => x.usuarioId === usuario?.usuarioId).length > 0;
  }, [getConnectedUsers, usuario?.usuarioId]
  );

  // MARK: carregarMinhaPessoa
  const carregarMinhaPessoa = useCallback(async (user: TokenKdsModel, tentativa: number): Promise<RetornoApiModel> => {
    return new Promise<RetornoApiModel>(async (resolve, reject) => {
      const minhaPessoa = await getUsuarioPessoa(user.usuarioId);
      if (!minhaPessoa.resultado?.data) {
        if (tentativa < 10) {
          setTimeout(() => {
            carregarMinhaPessoa(user, tentativa + 1)
              .then(e => {
                resolve(e);
              })
              .catch(e => {
                reject(e);
              })
          }, tentativa * 200);
        } else {
          reject(new Error("Seu acesso não está disponível para uso. Por favor, tente realizar o login em alguns instantes. Código: GET-MinhaPessoa"));
        }
        return;
      }
      resolve(minhaPessoa);
    });
  }, [getUsuarioPessoa])

  // MARK: carregarDadosMinhaPessoa
  const carregarDadosMinhaPessoa = useCallback(async (user: TokenKdsModel) => {
    const minhaPessoa = await carregarMinhaPessoa(user, 1);

    //SALVO O PESSOA USUARIO
    persistPessoaUsuario({
      pessoa: minhaPessoa.resultado?.data
    });
  }, [carregarMinhaPessoa, persistPessoaUsuario])

  const carregarDadosUsuario = React.useCallback(
    async (user: TokenKdsModel) => {
      try {
        await carregarDadosMinhaPessoa(user);
        setLoadingManual(true);
        const perfilIds = Array<string>();
        //PREENCHO OS PERFIS DELE (DISTINCT)
        if (user?.empresa) {
          for (let i = 0; i < user?.empresa.length; i++) {
            if (perfilIds.indexOf(user?.empresa[i]?.Perfil?.Id) === -1) {
              perfilIds.push(user?.empresa[i]?.Perfil?.Id);
            }
          }
        }

        const perfis = new Array<PerfilModel>();
        //FACO A BUSCA NA API PELOS PERFIS DO USUARIO
        for (let i = 0; i < perfilIds.length; i++) {
          const retApiPerfil = await getPerfilById(perfilIds[i]);
          if (retApiPerfil.erro) {
            throw retApiPerfil.erro;
          }
          perfis.push(retApiPerfil.resultado?.data);
        }

        //SALVA OS PERFIS!
        persistPerfilPermissoes(perfis);

        setLoadingManual(false);
      } catch (e: any) {
        throw e;
      } finally {
      }
    },
    [carregarDadosMinhaPessoa, persistPerfilPermissoes, getPerfilById],
  );

  const refreshUser = React.useCallback(
    async (refreshToken: string): Promise<boolean> => {
      try {
        const retRefresh = await postIdentidadeRefreshToken(refreshToken);

        if (retRefresh?.resultado?.data?.accessToken) {
          const newuser = convertToken(
            retRefresh?.resultado?.data?.accessToken,
          );
          if (newuser) {
            if (validaUsuarioConectado()) {
              addConnectedUser(newuser);
            }

            persistToken(newuser);
            await carregarDadosUsuario(newuser);
            setarUsuario(newuser);
            return true;
          }
        }
      } catch (e: any) {
        persistToken(undefined);
        persistPerfilPermissoes(undefined);
        persistPlanoUsuario(undefined);
        persistTermosDeUso(undefined, undefined);
        setPlano(undefined);
        setarUsuario(undefined);
        throw e;
      }
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );


  const getEmpresaSelecionada = useCallback(():
    | TokenKdsEmpresaModel
    | undefined => {
    const emp = usuario?.empresa.filter((x) => x.Id === getEmpresaAtual().id);
    if (emp === null || emp?.length === 0) return undefined;
    if (emp) return emp[0];

    return undefined;
  },
    [getEmpresaAtual, usuario?.empresa]
  );

  // MARK: getEmpresaIdWrapper
  const getEmpresaIdWrapper = useCallback(async (empresaId: string) => {

    const res = await getEmpresaId(empresaId)

    return res.resultado?.data as EmpresaModel

  }, [getEmpresaId])

  // MARK: getEmpresaConfig
  const getEmpresaConfig = useCallback(
    async (empresaId: string) => {
      const res = await getConfigEmpresaId(empresaId);

      if (res.erro) {
        throw res.erro;
      }

      const data = res.resultado?.data as ConfigEmpresaModel[];

      return data
    },
    [getConfigEmpresaId],
  );

  // MARK:getContratoConfig
  const getContratoConfig = useCallback(
    async () => {
      try {
        const res = await getConfigContrato(getPessoaUsuario().pessoa?.contratoId || '');

        if (res.erro) {
          throw res.erro;
        }

        const data = res.resultado?.data as ConfigEmpresaModel[];

        return data
      } catch (e: any) {
        showToast('error', e.message)
      }
    },
    [getConfigContrato, getPessoaUsuario, showToast],
  );


  const selecionarEmpresa = useCallback(
    async (empresaId: string): Promise<void> => {
      const empresa = await getEmpresaIdWrapper(empresaId)
      const empresaConfig = await getEmpresaConfig(empresaId);
      const contratoConfig = await getContratoConfig();

      if (contratoConfig) {
        setRegistro(KdsStorageKeys.ConfigContrato, new ContratoConfigCompletoModel(
          usuario?.empresa[0].ContratoId,
          contratoConfig
        ), false)
      }
      const empresaAtual: EmpresaCompletaModel = {
        ...empresa,
        configuracoes: empresaConfig,
      }
      persistEmpresaSelecionada(empresaAtual);
    },
    [getContratoConfig, getEmpresaConfig, getEmpresaIdWrapper, persistEmpresaSelecionada, setRegistro, usuario?.empresa],
  );

  const setarUser = useCallback(
    async (token: string, manterConectado: boolean, empresaId?: string) => {
      const user = convertToken(token);


      const empresaAtual = getRegistro(KdsStorageKeys.EmpresaAtual, false)

      let mesmaEmpresa = user?.empresa.find(empresas => empresas.Id === empresaAtual?.id)?.Id

      if (mesmaEmpresa !== empresaAtual?.id) {
        delRegistro(KdsStorageKeys.EmpresaAtual, false)
        delRegistro(KdsStorageKeys.ProcessosSetores, false)
      }

      if (!isTokenValid(user))
        throw new Error(
          'O usuário informado não é válido ou expirou seu acesso. (InvalidToken)',
        );
      //PERSISTE NO STORAGE
      persistToken(user);

      const plano = await refreshPlanoUsuario(token);

      if (!isPlanoControleProducao(plano?.plano)) {
        throw new Error('Seu plano não tem acesso a este sistema.')
      }

      //FAZEMOS A CONSULTA DOS DADOS NECESSÁRIOS DO USUARIO
      if (user) {
        await carregarDadosUsuario(user);
      }
      if (manterConectado) {
        if (user !== undefined) addConnectedUser(user);
      }
      //SELECIONO A EMPRESA
      const empresaSelecionadaNoContrato = user?.empresa.filter((x) => x.Id === getEmpresaAtual()?.id);
      const naoTemEmpresaContrato = (empresaSelecionadaNoContrato === null || empresaSelecionadaNoContrato?.length === 0)

      if (naoTemEmpresaContrato) {
        //DELETO OS FAVORITOS PQ ELE TROCOU DE USUARIO E ESTÁ EM OUTRO CONTRATO
        //SELECIONA A PRIMEIRA EMPRESA DO CONTRATO
        if (user?.empresa && naoTemEmpresaContrato && !empresaId) {
          const empresaList = user.empresa
          if (empresaList.length > 0) {
            await selecionarEmpresa(empresaList[0].Id)
            delRegistro(KdsStorageKeys.DocumentoContrato)
          } else {
            delRegistro(KdsStorageKeys.DocumentoContrato)
          }
          if (user.empresa.length === 1) {
            await selecionarEmpresa(user?.empresa[0].Id);
          }
        } else if (empresaId) {
          await selecionarEmpresa(empresaId);
        }
      } else if (empresaId) {
        await selecionarEmpresa(empresaId);
      }


      //SETAMOS O USUARIO
      setarUsuario(user);
    },
    [convertToken, getRegistro, isTokenValid, persistToken, refreshPlanoUsuario, delRegistro, carregarDadosUsuario, addConnectedUser, getEmpresaAtual, selecionarEmpresa],
  );

  const logar = React.useCallback(
    async (
      usuario: string,
      senha: string,
      contratoId: string,
      manterConectado: boolean,
    ): Promise<string | any> => {
      try {
        setLoadingManual(true);
        const retAuth = await auth({ usuario, senha, contratoId })

        //Usuario não confirmado está sem autorização
        if (retAuth.statusCode === 401) {
          throw new ApiError(retAuth.statusCode, retAuth.erro.message);
        }

        if (retAuth.statusCode === 300) {
          return retAuth.resultado?.data as MultiContratoModel
        }

        if (retAuth.statusCode === 402) {
          return {
            status: retAuth.statusCode,
            data: retAuth.resultado?.data
          }
        }

        if (retAuth.erro) {
          throw retAuth.erro;
        }

        await setarUser(retAuth?.resultado?.data?.accessToken, manterConectado);
        return retAuth?.resultado?.data?.accessToken
      } catch (e) {
        throw e;
      } finally {
        setLoadingManual(false);
      }
    },
    [auth, setarUser]
  );

  // MARK: getPermissaoBoolean
  const getPermissaoBoolean = useCallback(
    (codigo: EnumCodigosPermissoes): boolean => {
      const emp = getEmpresaSelecionada();
      if (isEmpty(emp)) return false;

      const perfis = getPerfilPermissoes();
      if (isEmpty(perfis)) return false;

      const perfil = perfis.filter((x) => x.id === emp?.Perfil.Id)[0];

      let totPerm = new Array<PerfilPermissaoModel>();
      if (perfil.permissoes) {
        //CRIO UM OBJ APENAS COM OS ARRAYS
        perfil.permissoes.forEach((permissao) => {
          totPerm = totPerm.concat(permissao);
        });
      }

      const filter = totPerm.filter((x) => x.codigo === codigo);
      if (isEmpty(filter)) return false;

      return filter[0].valor === 'true';
    },
    [getEmpresaSelecionada, getPerfilPermissoes],
  );

  // MARK: getPermissaoString
  const getPermissaoString = useCallback(
    (codigo: EnumCodigosPermissoes): string => {
      const emp = getEmpresaSelecionada();
      if (isEmpty(emp)) return '';

      const perfis = getPerfilPermissoes();
      if (isEmpty(perfis)) return '';

      const perfil = perfis.filter((x) => x.id === emp?.Perfil.Id)[0];
      if (!perfil.permissoes) return '';

      let totPerm = new Array<PerfilPermissaoModel>();
      //CRIO UM OBJ APENAS COM OS ARRAYS
      if (perfil.permissoes) {
        //CRIO UM OBJ APENAS COM OS ARRAYS
        perfil.permissoes.forEach((permissao) => {
          totPerm = totPerm.concat(permissao);
        });
      }

      const filter = totPerm.filter((x) => x.codigo === codigo);
      if (isEmpty(filter)) return '';

      return filter[0].valor;
    },
    [getEmpresaSelecionada, getPerfilPermissoes],
  );

  return (
    <SessaoAtualContext.Provider
      value={{
        usuario,
        plano,
        getEmpresaSelecionada,
        getPerfil: getPerfilPermissoes,
        carregando: loading,
        deslogar,
        setarUser,
        carregarDadosUsuario,
        refreshUser,
        sessaoExpirada,
        logar,
        converterToken,
        validaUsuarioConectado,
        termosDeUsoAtivo,
        selecionarEmpresa,
        refreshPlanoUsuario,
        getPermissaoBoolean,
        getPermissaoString,
      }}
    >
      {children}
    </SessaoAtualContext.Provider>
  );
};